/** EquipaPRO brand constants — single source of truth for contact info and assets. */

import { STORAGE_MEDIA_ORIGIN } from "@/lib/mediaRecovery";
import { SEO_PAGEVIEW_READY_EVENT } from "@/lib/googleAnalytics";

export const WHATSAPP_NUMBER_DISPLAY = "(48) 99158-3576";
export const WHATSAPP_URL =
  "https://wa.me/5548991583576?text=" +
  encodeURIComponent(
    "Olá! Vim pelo site da EquipaPRO e gostaria de falar com o comercial.",
  );
export const EMAIL = "comercial@equipapro.com.br";
export const CTA_TEXT = "Vem ser PRO";
export const SLOGAN = "Inteligência em cada porta. Confiança em cada acesso.";
export const CITY = "Garopaba – SC";
export const ADDRESS = "SC 434 - Rod. Abílio Manoel de Lima, 3128 — Garopaba, SC, 88495-000";
export const FACTORY_COORDS = { lat: -28.0498675, lng: -48.6420026 };

export const ASSETS = {
  logoOrange: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/aEUBmpBCDZKZqgom.webp",
  logoWhite: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/XZHBqAhzDFrckcPc.webp",
  monogramEP: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/jHamwWiauTdloUfv.webp",
  facade: `https://equipapro-zblaoftc.manus.space/manus-storage/fachada-equipapro-v2_7fbb4d8c_59e189a4.jpg`,
  revolvingDoor: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/WiXTjQiAaLNQMdaG.webp",
  heroPremium: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/IcUlKyGlPUGmGfxh.png",
  brazilMap: `https://equipapro-zblaoftc.manus.space/manus-storage/mapa-brasil-conexoes_b6bf898b_651504bd.webp`,
} as const;

export const SOCIAL_LINKS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/equipapro/",
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/equipapro.com.br/",
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/equipapro-seguranca-e-tecnologia-da-informacao-ltda",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@Equipapro",
  },
] as const;

/** Fotos de instalações entregues — adicionar novas imagens aqui conforme o usuário enviar. */
export const INSTALLATION_PHOTOS = [
  {
    src: ASSETS.revolvingDoor,
    alt: "Porta giratória Prime EquipaPRO instalada em cooperativa",
    caption: "Projeto entregue · Porta giratória Prime",
  },
  {
    src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/BwqcOMjjIjPWFaTS.webp",
    alt: "Porta automática de vidro EquipaPRO instalada em entrada corporativa",
    caption: "Projeto entregue · Porta automática de vidro",
  },
  {
    src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/eKNjuksTfdjTrLVz.webp",
    alt: "Eclusa de segurança detectora de metais EquipaPRO instalada em joalheria",
    caption: "Projeto entregue · Eclusa de segurança detectora de metais",
  },
  {
    src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/ZcKoPQqfNqeBzjXg.webp",
    alt: "Catraca de controle de acesso EquipaPRO instalada em condomínio",
    caption: "Projeto entregue · Catraca de controle de acesso",
  },
  {
    src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663042190274/WIVQpcsCvSFajafT.webp",
    alt: "Porta giratória detectora de metais EquipaPRO instalada em agência bancária",
    caption: "Projeto entregue · Porta giratória detectora de metais",
  },
  {
    src: `https://equipapro-zblaoftc.manus.space/manus-storage/instalacao-detectores-evento_edf69be5_a73f99f8.webp`,
    alt: "Portais detectores de metais EquipaPRO instalados em evento — Oktoberfest",
    caption: "Projeto entregue · Portais detectores de metais em evento",
  },
] as const;

export const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "Sobre", href: "/sobre" },
  { label: "Produtos", href: "/produtos" },
  { label: "Aplicações", href: "/aplicacoes" },
  { label: "Serviços", href: "/servicos" },
  { label: "Tecnologias", href: "/tecnologias" },
  { label: "Parceiros", href: "/parceiros" },
  { label: "Blog", href: "/blog" },
  { label: "Contato", href: "/contato" },
] as const;

export const SITE_NAME = "EquipaPRO";

/** Atualiza title, meta description e canonical por página (SPA). */
function setMeta(selector: string, attr: string, value: string) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    // parse selector like meta[property="og:title"] or meta[name="description"]
    const propMatch = selector.match(/\[property=["']([^"']+)["']\]/);
    const nameMatch = selector.match(/\[name=["']([^"']+)["']\]/);
    if (propMatch) el.setAttribute("property", propMatch[1]);
    else if (nameMatch) el.setAttribute("name", nameMatch[1]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export function setPageSeo(opts: { title: string; description?: string; path?: string; image?: string }) {
  document.title = opts.title;
  const canonical = opts.path !== undefined
    ? `${window.location.origin}${canonicalPath(opts.path)}`
    : `${window.location.origin}${canonicalPath(window.location.pathname)}`;

  if (opts.description) {
    setMeta('meta[name="description"]', "content", opts.description);
    setMeta('meta[property="og:description"]', "content", opts.description);
    setMeta('meta[name="twitter:description"]', "content", opts.description);
  }
  setMeta('meta[property="og:title"]', "content", opts.title);
  setMeta('meta[name="twitter:title"]', "content", opts.title);
  setMeta('meta[property="og:url"]', "content", canonical);
  if (opts.image) {
    setMeta('meta[property="og:image"]', "content", opts.image);
    setMeta('meta[name="twitter:image"]', "content", opts.image);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
  }
  if (opts.path !== undefined) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
  }

  window.dispatchEvent(
    new CustomEvent(SEO_PAGEVIEW_READY_EVENT, {
      detail: {
        path: opts.path !== undefined ? canonicalPath(opts.path) : canonicalPath(window.location.pathname),
        title: opts.title,
        url: window.location.href,
      },
    })
  );
}

/** URL de WhatsApp com mensagem personalizada. */
export function whatsappUrl(message: string) {
  return `https://wa.me/5548991583576?text=${encodeURIComponent(message)}`;
}
import { canonicalPath } from "@shared/canonicalPath";
